import * as commentsApi from '@/api/endpoints/comments';
import type { Comment } from '@/types/comment';
import type { FeedItem } from '@/types/feed';
import type { Paginated } from '@/types/pagination';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { FEED_QUERY_KEYS } from './use-activity';

export const COMMENT_QUERY_KEYS = {
  all: ['comments'] as const,
  forCheckIn: (checkInId: string) => ['comments', checkInId] as const,
};

interface UseCommentsOptions {
  /**
   * Gate the query. Feed cards pass `enabled: expanded` so the full comment
   * list is only fetched once a card is opened -- the preview renders from
   * `item.recentComments`, so a 20-card page no longer fires 20 idle requests.
   * Defaults to `true` to preserve existing call sites.
   */
  enabled?: boolean;
}

/**
 * Hook to fetch paginated comments for a check-in
 */
export function useComments(
  checkInId: string,
  { enabled = true }: UseCommentsOptions = {},
) {
  return useInfiniteQuery({
    queryKey: COMMENT_QUERY_KEYS.forCheckIn(checkInId),
    queryFn: async ({ pageParam }): Promise<Paginated<Comment>> => {
      return commentsApi.getComments(checkInId, pageParam);
    },
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
  });
}

/**
 * Hook to create a comment with optimistic feed cache update
 */
export function useAddComment() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      checkInId,
      content,
    }: {
      checkInId: string;
      content: string;
    }) => {
      return commentsApi.createComment(checkInId, content);
    },

    onMutate: async ({ checkInId }) => {
      // Cancel outgoing feed refetches
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });

      // Snapshot feed data
      const previousFeedData = queryClient.getQueryData<
        InfiniteData<Paginated<FeedItem>>
      >(FEED_QUERY_KEYS.feed());

      // Optimistically increment commentsCount in feed cache
      queryClient.setQueryData<InfiniteData<Paginated<FeedItem>>>(
        FEED_QUERY_KEYS.feed(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((item) => {
                if (item.checkIn.id !== checkInId) return item;
                return {
                  ...item,
                  commentsCount: item.commentsCount + 1,
                };
              }),
            })),
          };
        }
      );

      return { previousFeedData };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousFeedData) {
        queryClient.setQueryData(
          FEED_QUERY_KEYS.feed(),
          context.previousFeedData
        );
      }
    },

    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: COMMENT_QUERY_KEYS.forCheckIn(variables.checkInId),
      });
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    },
  });

  return {
    addComment: (checkInId: string, content: string) =>
      mutation.mutateAsync({ checkInId, content }),
    isSubmitting: mutation.isPending,
  };
}

/**
 * Hook to delete a comment with optimistic feed cache update
 */
export function useDeleteComment() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      commentId,
    }: {
      commentId: string;
      checkInId: string;
    }) => {
      return commentsApi.deleteComment(commentId);
    },

    onMutate: async ({ commentId, checkInId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });
      await queryClient.cancelQueries({
        queryKey: COMMENT_QUERY_KEYS.forCheckIn(checkInId),
      });

      // Snapshot
      const previousFeedData = queryClient.getQueryData<
        InfiniteData<Paginated<FeedItem>>
      >(FEED_QUERY_KEYS.feed());

      const previousComments = queryClient.getQueryData<
        InfiniteData<Paginated<Comment>>
      >(COMMENT_QUERY_KEYS.forCheckIn(checkInId));

      // Optimistically decrement commentsCount in feed cache
      queryClient.setQueryData<InfiniteData<Paginated<FeedItem>>>(
        FEED_QUERY_KEYS.feed(),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((item) => {
                if (item.checkIn.id !== checkInId) return item;
                return {
                  ...item,
                  commentsCount: Math.max(0, item.commentsCount - 1),
                };
              }),
            })),
          };
        }
      );

      // Optimistically remove comment from comments cache
      queryClient.setQueryData<InfiniteData<Paginated<Comment>>>(
        COMMENT_QUERY_KEYS.forCheckIn(checkInId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.filter((c) => c.id !== commentId),
            })),
          };
        }
      );

      return { previousFeedData, previousComments };
    },

    onError: (_error, variables, context) => {
      if (context?.previousFeedData) {
        queryClient.setQueryData(
          FEED_QUERY_KEYS.feed(),
          context.previousFeedData
        );
      }
      if (context?.previousComments) {
        queryClient.setQueryData(
          COMMENT_QUERY_KEYS.forCheckIn(variables.checkInId),
          context.previousComments
        );
      }
    },

    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: COMMENT_QUERY_KEYS.forCheckIn(variables.checkInId),
      });
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    },
  });

  return {
    deleteComment: (commentId: string, checkInId: string) =>
      mutation.mutate({ commentId, checkInId }),
    isDeleting: mutation.isPending,
  };
}
